import React, { useState } from 'react';
import { Button, Col, DatePicker, Form, Input, message, Modal, Row, Select, Space } from 'antd';
import axios from 'axios';
import { profileRoute } from '../../../lib/routes/profile';
import LocalStorageService from '../../../services/LocalStorageService';
import ProfileImageNew from '../../ProfileImageNew/ProfileImage';
import { useAppSelector } from '../../../store/hooks';
import { ReactComponent as EmailIcon } from '../../../lib/img/icon/profile/email.svg';
import { ReactComponent as PhoneIcon } from '../../../lib/img/icon/profile/phone.svg';
import { ReactComponent as MetroIcon } from '../../../lib/img/icon/profile/metro.svg';
import { ReactComponent as HouseIcon } from '../../../lib/img/icon/profile/house.svg';
import { ReactComponent as CalendarIcon } from '../../../lib/img/icon/profile/calendar.svg';
import { ReactComponent as PencilLineIcon } from '../../../lib/img/pencil_line.svg';
import usePhoneMask from '../../../lib/utils/usePhoneMask';
import { uniqueId } from 'lodash';
import './UserProfileInfo.less';
import { displayFormat, toDisplayFormat, toYearMonthDayLineFormat } from '../../../lib/utils/formatDate';
import { getDictionaryItemById } from '../../../lib/utils/applications';
import { ReactComponent as UrgentIcon } from '../../../lib/img/urgent-icon.svg';
import moment from 'moment';
import { MaskedInput } from 'antd-mask-input';
import InputMask from 'react-input-mask';
import { datePikerMaskDefault } from '../../../lib/const/masks';
import { EmailCooldownTimer } from './EmailCooldownTimerProps/EmailCooldownTimerProps';
import selectFilterOption from '../../../lib/utils/selectFilterOption';
import SuggestInput from '../../SuggestInput/SuggestInput';
import RequiredMark from '../../common/RequiredMark/RequiredMark';

interface IProps {
  image?: string;
  handleNewImage: (file, route) => void;
  isImageLoading: boolean;
  handleConfirmEmail: () => void;
  isEmailConfirmDisabled?: boolean;
  handleEditProfile: (fields, isEditField, callbackSuccess, callbackError) => void;
  loadAvatar?: () => void;
}

const UserProfileInfo = React.memo(
  ({
    image,
    handleNewImage,
    isImageLoading,
    handleConfirmEmail,
    isEmailConfirmDisabled,
    handleEditProfile,
    loadAvatar,
  }: IProps) => {
    const { personProfileShortInfo } = useAppSelector((state) => state.persons);
    const { personStatuses, personTypes, subwayStation } = useAppSelector((state) => state.dictionaries);

    const { getMaskedPhoneNumberForProfile, phoneMaskForProfile, phoneInputPlaceholderForProfile } = usePhoneMask();

    const [profileForm] = Form.useForm();

    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isEditField, setIsEditField] = useState({ status: false, name: '' });

    const onFinish = (fields) => {
      const onCancel = () => {
        setIsEditField((prev) => ({ ...prev, status: false, name: '' }));
        profileForm.resetFields();
      };

      const onOk = () => {
        handleEditProfile(
          fields,
          isEditField.name,
          () => {
            setIsEditField((prev) => ({ ...prev, status: false, name: '' }));
          },
          () => {
            onCancel();
          }
        );
      };

      switch (isEditField.name) {
        case 'phone':
          const clearPhone = (phone: string) => phone.replace(/\D/g, ''); // удаляет все, кроме цифр

          const isEqual = clearPhone(fields.phone) === clearPhone(personProfileShortInfo?.phone || '');
          if (isEqual) {
            onCancel();
            return;
          } else {
            onOk();
            return;
          }
        case 'email':
          if (fields.email === personProfileShortInfo?.email) {
            onCancel();
            return;
          } else {
            onOk();
            return;
          }
        case 'subwayStationId':
          if (fields.subwayStationId === personProfileShortInfo?.address?.subwayStationId) {
            onCancel();
            return;
          } else {
            onOk();
            return;
          }
        case 'unstructuredAddress':
          const isChangeUnstructuredAddress =
            fields.unstructuredAddress !== personProfileShortInfo?.address?.unstructuredAddress;
          const isChangeApartment = fields.apartment !== personProfileShortInfo?.address?.apartment;

          if (!isChangeUnstructuredAddress && !isChangeApartment) {
            onCancel();
            return;
          } else {
            if (isChangeUnstructuredAddress || isChangeApartment) onOk();
            return;
          }
        case 'birthDate':
          if (
            toYearMonthDayLineFormat(moment(fields.birthDate, 'DD.MM.YYYY')) ===
            toYearMonthDayLineFormat(moment(personProfileShortInfo?.birthDate))
          ) {
            onCancel();
            return;
          } else {
            onOk();
            return;
          }
        default:
          onCancel();
          break;
      }
    };

    const onSave = () => {
      profileForm.submit();
    };

    const onChange = ({ name }) => {
      profileForm.resetFields();
      setIsEditField((prev) => ({ ...prev, status: true, name }));

      if (name === 'birthDate') {
        setTimeout(() => {
          profileForm.validateFields(['birthDate']);
        }, 0);
      }
    };

    const onCancel = () => {
      setIsEditField((prev) => ({ ...prev, status: false }));
      profileForm.resetFields();
    };

    const renderDeleteModal = () => {
      return (
        <Modal open={isDeleteModalVisible} footer={false} closable={false} width={615}>
          <p>
            Удаление профиля приведёт к удалению учетной записи и персональных данных. <br />
            Вы подтверждаете изменение?
          </p>
          <div style={{ textAlign: 'center' }}>
            <Space>
              <Button onClick={() => setIsDeleteModalVisible(false)}>Отмена</Button>
              <Button
                type="primary"
                onClick={() => {
                  axios
                    .delete(profileRoute())
                    .then(() => {
                      LocalStorageService.clearToken();
                      window.location.reload();
                      message.success('Ваш профиль удалён', 3);
                    })
                    .catch(() => {
                      message.error('Произошла ошибка. Пожалуйста, попробуйте позднее', 3);
                    });
                }}
              >
                Подтвердить
              </Button>
            </Space>
          </div>
        </Modal>
      );
    };

    const isValidDate = (dateString) => {
      const [day, month, year] = dateString.split('.');

      if (!day || !month || !year) {
        return false;
      }

      const date = new Date(`${year}-${month}-${day}`);

      return !isNaN(date.getTime());
    };

    const fields = [
      {
        name: 'email',
        value: (
          <Row align="middle" wrap={false}>
            <span
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {personProfileShortInfo?.email}
            </span>
            <span>
              {!personProfileShortInfo?.emailConfirmation && (
                <Row
                  align="middle"
                  wrap={false}
                  className={`cursor-pointer ${isEmailConfirmDisabled ? 'email-confirm-disabled' : ''}`}
                >
                  <UrgentIcon width={16} height={16} style={{ marginLeft: '16px', minWidth: '16px' }} />
                  <span className="info_error ml4" onClick={handleConfirmEmail}>
                    Подтвердить
                  </span>
                </Row>
              )}
            </span>
          </Row>
        ),
        icon: (
          <RequiredMark>
            <EmailIcon />
          </RequiredMark>
        ),
        valueEdit: (
          <Form.Item
            name="email"
            className="user-profile-info__additional-block__content__email"
            rules={[
              {
                required: true,
                type: 'email',
                message: '',
              },
            ]}
          >
            <Input placeholder="name@mail.com" autoFocus />
          </Form.Item>
        ),
        button:
          isEmailConfirmDisabled && !personProfileShortInfo?.emailConfirmation ? (
            <div className="display-flex" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ textAlign: 'center', color: 'rgba(42, 42, 42, 0.5)', minWidth: 'max-content' }}>
                Отправить новую ссылку можно через&nbsp;
                <EmailCooldownTimer />
              </span>
            </div>
          ) : null,
        classNameParent: 'email',
      },
      {
        name: 'phone',
        value: personProfileShortInfo?.phone && getMaskedPhoneNumberForProfile(personProfileShortInfo.phone),
        icon: (
          <RequiredMark>
            <PhoneIcon />
          </RequiredMark>
        ),
        valueEdit: (
          <Form.Item
            name="phone"
            className="user-profile-info__additional-block__content__phone"
            rules={[
              {
                required: true,
                message: '',
              },
            ]}
          >
            <MaskedInput
              mask={[{ mask: phoneMaskForProfile }]}
              name="phone"
              //TODO Eugene. Разобраться: без указания prefix=" " не срабатывает подсветка валидации
              prefix=" "
              placeholder={phoneInputPlaceholderForProfile}
            />
          </Form.Item>
        ),
        classNameParent: 'phone',
      },
      {
        name: 'subwayStationId',
        value: getDictionaryItemById(subwayStation, personProfileShortInfo?.address?.subwayStationId)?.name,
        icon: (
          <RequiredMark>
            <MetroIcon />
          </RequiredMark>
        ),
        valueEdit: (
          <Form.Item name="subwayStationId" className="user-profile-info__additional-block__content__subway">
            <Select placeholder="Станция метро" showSearch filterOption={selectFilterOption} autoFocus>
              {subwayStation
                .filter((el) => !el.deleted)
                .map(({ id, name }) => (
                  <Select.Option key={id} value={id}>
                    {name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        classNameParent: 'subwayStationId',
      },
      {
        name: 'unstructuredAddress',
        classNameParent: 'unstructuredAddress',
        value: (
          <Row gutter={[16, 6]} className="user-profile-info__additional-block__content__address">
            <Col span={24}>
              <div className="field-group">
                <div className="field-label">
                  <RequiredMark>Адрес</RequiredMark>&nbsp;&nbsp;&nbsp;&nbsp;:
                </div>
                <div className="field-box">
                  <span title={personProfileShortInfo?.address?.unstructuredAddress || '-'}>
                    {personProfileShortInfo?.address?.unstructuredAddress || '-'}
                  </span>
                </div>
              </div>
            </Col>
            <Col span={24}>
              <div className="field-group">
                <div className="field-label">Квартира:</div>
                <div className="field-box">
                  <span title={personProfileShortInfo?.address?.apartment || undefined}>
                    <Row className="user-profile-info__additional-block__content__address__apartment">
                      {personProfileShortInfo?.address?.apartment ? (
                        <span
                          style={{ whiteSpace: 'nowrap' }}
                          className="user-profile-info__additional-block__content__address__apartment__same"
                        >
                          {personProfileShortInfo?.address?.apartment}
                        </span>
                      ) : (
                        <>
                          <span
                            style={{ whiteSpace: 'nowrap' }}
                            className="user-profile-info__additional-block__content__address__apartment__notSame"
                          >
                            _
                          </span>
                          <span
                            style={{ whiteSpace: 'nowrap' }}
                            className="user-profile-info__additional-block__content__address__apartment__notSame"
                          >
                            _
                          </span>
                        </>
                      )}
                    </Row>
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        ),
        icon: <HouseIcon />,
        valueEdit: (
          <Row gutter={[16, 6]} className="user-profile-info__additional-block__content__address">
            <Col span={24}>
              <div className="field-group">
                <div className="field-label">
                  <RequiredMark>Адрес</RequiredMark>&nbsp;&nbsp;&nbsp;&nbsp;:
                </div>
                <div className="field-box">
                  <SuggestInput
                    disableOnBlur={true}
                    name="unstructuredAddress"
                    required
                    label=""
                    placeholder="Город, улица, дом, строение, корпус"
                    allowClear={false}
                  />
                </div>
              </div>
            </Col>
            <Col span={24}>
              <div className="field-group">
                <div className="field-label">Квартира:</div>
                <div className="field-box">
                  <Form.Item
                    name="apartment"
                    className="user-profile-info__additional-block__content__address__apartment"
                  >
                    <Input placeholder="__" maxLength={4} />
                  </Form.Item>
                </div>
              </div>
            </Col>
          </Row>
        ),
      },
      {
        name: 'birthDate',
        value: (
          <Row gutter={[16, 6]} className="user-profile-info__additional-block__content__birth-date">
            <Col span={24}>
              <div className="field-group">
                <div className="field-label">Дата рождения:</div>
                <div className="field-box">
                  <span title={personProfileShortInfo?.birthDate && toDisplayFormat(personProfileShortInfo?.birthDate)}>
                    {personProfileShortInfo?.birthDate && toDisplayFormat(personProfileShortInfo?.birthDate)}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        ),
        icon: (
          <RequiredMark>
            <CalendarIcon />
          </RequiredMark>
        ),
        valueEdit: (
          <Row gutter={[16, 6]} className="user-profile-info__additional-block__content__birth-date">
            <Col span={24}>
              <div className="field-group">
                <div className="field-label">Дата рождения:</div>
                <div className="field-box">
                  <Form.Item
                    name="birthDate"
                    className="user-profile-info__additional-block__content__birth-date"
                    rules={[
                      {
                        required: true,
                        message: '',
                      },
                      () => ({
                        validator(_, value) {
                          if (!value) {
                            return Promise.resolve();
                          }

                          // Проверка формата ДД.ММ.ГГГГ
                          if (!isValidDate(value)) {
                            return Promise.reject('Неверный формат');
                          }

                          // Проверка на существующую дату
                          const [day, month, year] = value.split('.');
                          const date = new Date(`${year}-${month}-${day}`);
                          const isValid =
                            date && date.getMonth() + 1 === parseInt(month) && date.getDate() === parseInt(day);

                          if (!isValid) {
                            return Promise.reject('Неверный формат');
                          }

                          // Проверка возраста (18-100 лет)
                          const today = new Date();
                          const birthDate = new Date(`${year}-${month}-${day}`);
                          let age = today.getFullYear() - birthDate.getFullYear();
                          const monthDiff = today.getMonth() - birthDate.getMonth();

                          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                            age--;
                          }

                          if (age < 18 || age > 100) {
                            return Promise.reject('Неверный формат');
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                    style={{ display: 'flex', justifyContent: 'start' }}
                  >
                    <InputMask
                      mask={datePikerMaskDefault}
                      maskChar={null}
                      placeholder="ДД.ММ.ГГГГ"
                      allowClear={false}
                    />
                  </Form.Item>
                </div>
              </div>
            </Col>
          </Row>
        ),
      },
    ];

    return (
      <div className="user-profile-info-container ml20 mr20" style={{ width: '100%' }}>
        {renderDeleteModal()}
        <Row className="user-profile-info__main-block">
          <div className="user-profile-info__main-block__image">
            <ProfileImageNew
              myProfile={true}
              image={image}
              handleNewImage={handleNewImage}
              isImageLoading={isImageLoading}
              heightPhoto={240}
              widthPhoto={240}
              loadAvatar={loadAvatar}
            />
          </div>
          <div className="user-profile-info__main-block__info">
            <Row
              gutter={[16, 16]}
              className="user-profile-info__main-block__info__profile-wrapper mb60"
              style={{ marginTop: '-5px' }}
            >
              <Col span={12}>
                <div className="field-group">
                  <div className="field-label">Имя</div>
                  <div className="field-box">
                    <span title={personProfileShortInfo?.firstName}>{personProfileShortInfo?.firstName}</span>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                {personProfileShortInfo?.middleName && (
                  <div className="field-group">
                    <div className="field-label">Отчество</div>
                    <div className="field-box">
                      <span title={personProfileShortInfo?.middleName}>{personProfileShortInfo?.middleName}</span>
                    </div>
                  </div>
                )}
              </Col>
              <Col span={12}>
                <div className="field-group">
                  <div className="field-label">Фамилия</div>
                  <div className="field-box">
                    <span title={personProfileShortInfo?.lastName}>{personProfileShortInfo?.lastName}</span>
                  </div>
                </div>
              </Col>
            </Row>
            <Row wrap={true} gutter={[16, 0]} className="user-profile-info__main-block__info__profile-wrapper">
              <Col span={8} className="field-group">
                <div className="field-label">Логин</div>
                <div className="field-box">
                  <span title={`${personProfileShortInfo?.login}`}>{personProfileShortInfo?.login}</span>
                </div>
              </Col>
              <Col span={8} className="field-group">
                <div className="field-label" title="Статус пользователя">
                  Статус пользователя
                </div>
                <div className="field-box">
                  <span
                    title={personStatuses.find((status) => status.id === personProfileShortInfo?.personStatusId)?.name}
                  >
                    {personStatuses.find((status) => status.id === personProfileShortInfo?.personStatusId)?.name}
                  </span>
                </div>
              </Col>
              <Col span={8} className="field-group">
                <div className="field-label" title="Тип пользователя">
                  Тип пользователя
                </div>
                <div className="field-box">
                  <span title={personTypes.find((type) => type.id === personProfileShortInfo?.personTypeId)?.name}>
                    {personTypes.find((type) => type.id === personProfileShortInfo?.personTypeId)?.name}
                  </span>
                </div>
              </Col>
            </Row>
          </div>
        </Row>

        <Form
          form={profileForm}
          initialValues={{
            email: personProfileShortInfo?.email,
            phone: personProfileShortInfo?.phone,
            subwayStationId: personProfileShortInfo?.address?.subwayStationId,
            birthDate: moment(personProfileShortInfo?.birthDate).format(displayFormat),
            unstructuredAddress: personProfileShortInfo?.address?.unstructuredAddress,
            apartment: personProfileShortInfo?.address?.apartment,
          }}
          onFinish={onFinish}
        >
          {fields.map((field) => (
            <Row className={`user-profile-info__additional-block ${field.classNameParent || ''}`} key={uniqueId()}>
              <Col className="user-profile-info__additional-block__icon">{field.icon}</Col>
              <Col className="user-profile-info__additional-block__content">
                {isEditField.status && isEditField.name === field.name ? field.valueEdit : field.value}
              </Col>
              <Col className="user-profile-info__additional-block__button">
                {isEditField.status && isEditField.name === field.name ? (
                  <Row wrap={false}>
                    <Button onClick={onCancel}>Отменить</Button>
                    <Button type="primary" onClick={onSave}>
                      Сохранить
                    </Button>
                  </Row>
                ) : (
                  field?.button || (
                    <div className={`${isEditField.status ? 'disabled-icon-parent' : ''}`}>
                      <div
                        className={`display-flex ${isEditField.status ? 'disabled-icon-children' : ''}`}
                        style={{ alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => onChange({ name: field.name })}
                      >
                        <PencilLineIcon className="mr4" />
                        <span style={{ textAlign: 'center' }}>Изменить</span>
                      </div>
                    </div>
                  )
                )}
              </Col>
            </Row>
          ))}
        </Form>

        <div className="pb12 display-flex" style={{ alignItems: 'start' }}>
          <Button style={{ width: 211, height: 40 }} onClick={() => setIsDeleteModalVisible(true)}>
            Удалить профиль
          </Button>
        </div>
      </div>
    );
  }
);

export default UserProfileInfo;
